import React, { useState } from 'react';
import { galleryImages } from '../data/mock';
import { useInView } from '../hooks/useInView';
import { X, ZoomIn, Camera } from 'lucide-react';

const GalleryPage = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1 });
  const [galleryRef, galleryInView] = useInView({ threshold: 0.1 });
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = ['all', ...new Set(galleryImages.map(img => img.category))];

  const filteredImages = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);

  const categoryColors = {
    'all': 'from-[#1F3C88] to-[#2A4FA8]',
    'conferences': 'from-emerald-400 to-green-600',
    'academic': 'from-blue-400 to-indigo-600',
    'professional': 'from-amber-400 to-orange-500',
    'research': 'from-purple-400 to-violet-600',
  };

  return (
    <>
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] py-32 overflow-hidden" ref={headerRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-rose-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-rose-400 to-pink-600 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-rose-500/30">
              VISUAL JOURNEY
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Photo <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-pink-300">Gallery</span>
            </h1>
            <p className="text-white/80 max-w-2xl text-xl leading-relaxed font-medium">
              Moments from conferences, trainings, research projects, and professional engagements.
            </p>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" ref={galleryRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Category Filter */}
          <div className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-700 ${galleryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2.5 rounded-full text-sm font-bold capitalize transition-all duration-300 ${activeCategory === cat ? `bg-gradient-to-r ${categoryColors[cat] || 'from-[#1F3C88] to-[#2A4FA8]'} text-white shadow-lg` : 'bg-white text-[#4A5568] hover:bg-gray-50 border-2 border-gray-200'}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 mb-12">
            <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-400 to-green-600 text-white px-6 py-3 rounded-full shadow-lg shadow-emerald-500/30">
              <Camera className="w-5 h-5" />
              <span className="font-bold">{filteredImages.length} Photos</span>
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredImages.map((image, index) => {
              const colors = [
                'from-emerald-400 to-green-600',
                'from-amber-400 to-orange-500',
                'from-blue-400 to-indigo-600',
                'from-purple-400 to-violet-600',
                'from-rose-400 to-pink-600',
                'from-cyan-400 to-teal-600',
              ];
              const colorGradient = colors[index % colors.length];
              
              return (
                <div
                  key={index}
                  className={`group relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-105 hover:-translate-y-2 shadow-lg hover:shadow-2xl ${galleryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${(index % 8) * 50}ms` }}
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-48 md:h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${colorGradient} opacity-0 group-hover:opacity-80 transition-opacity duration-300 flex flex-col items-center justify-center`}>
                    <ZoomIn className="w-10 h-10 text-white mb-2" />
                    <span className="text-white font-bold text-center px-4">{image.alt}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="text-white text-xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{selectedImage.alt}</p>
            <span className="text-white/60 font-medium capitalize">{selectedImage.category}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryPage;